(() => {
  if (window.__szhDesktopListInstalled) return;
  window.__szhDesktopListInstalled = true;

  const roots = '.section-szh-collection-product-grid, .section-szh-search-results';
  const states = new WeakMap();
  const chunkSize = 48;
  const isSearch = (root) => root.matches('.section-szh-search-results');
  const items = (root) => [...root.querySelectorAll('#product-grid > [data-szh-list-product]')];
  const identity = (item) => `${item.dataset.szhListProduct}:${item.dataset.szhListIndex}`;
  const signature = (root) => items(root).map(identity).join(',');

  const syncUrl = (root, view) => {
    if (isSearch(root)) return;
    const url = new URL(window.location.href);
    if (view === 'list') url.searchParams.set('szh_layout', 'list');
    else url.searchParams.delete('szh_layout');
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url);
  };

  const paint = (root, view) => {
    root.dataset.szhView = view;
    root.querySelectorAll('[data-szh-view-button]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.szhViewButton === view));
    });
    syncUrl(root, view);
  };

  const setBusy = (root, busy) => {
    root.querySelectorAll('[data-szh-view-button="list"]').forEach((button) => {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  };

  const loadList = async (root, state) => {
    const missing = items(root).filter((item) => !item.querySelector(':scope > [data-szh-list-card]'));
    if (!missing.length) {
      if (state.requested === 'list' && state.media.matches) paint(root, 'list');
      return;
    }

    state.controller?.abort();
    root.dataset.szhView = 'grid';
    const controller = new AbortController();
    state.controller = controller;
    const before = signature(root);
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    setBusy(root, true);

    try {
      const base = new URL(window.location.href);
      ['section_id', 'sections', 'szh_layout'].forEach((key) => base.searchParams.delete(key));
      base.searchParams.set('view', 'szh-list-data');
      const pages = [...new Set(missing.map((item) => Math.floor(Number(item.dataset.szhListIndex) / chunkSize) + 1))];
      const documents = await Promise.all(pages.map(async (page) => {
        const url = new URL(base);
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`List view returned ${response.status}`);
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      }));

      if (controller.signal.aborted || !root.isConnected || signature(root) !== before ||
          state.requested !== 'list' || !state.media.matches) return;

      const cards = new Map();
      documents.forEach((document) => {
        document.querySelectorAll('[data-szh-list-card]').forEach((card) => {
          cards.set(`${card.dataset.szhListCard}:${card.dataset.szhListIndex}`, card);
        });
      });
      if (missing.some((item) => !cards.has(identity(item)))) throw new Error('List view results changed');

      const sectionId = root.id.replace(/^shopify-section-/, '');
      missing.forEach((item) => {
        const card = document.importNode(cards.get(identity(item)), true);
        card.querySelectorAll('product-form').forEach((form) => { form.dataset.sectionId = sectionId; });
        card.querySelectorAll('[id]').forEach((node) => {
          node.setAttribute('id', node.getAttribute('id').replace('szh-lazy-list', sectionId));
        });
        item.appendChild(card);
      });
      paint(root, 'list');
      window.Shopify?.PaymentButton?.init?.();
    } catch (error) {
      if (state.controller !== controller) return;
      state.requested = 'grid';
      paint(root, 'grid');
      if (error.name !== 'AbortError') console.error('Desktop list could not be loaded:', error);
    } finally {
      window.clearTimeout(timeout);
      if (state.controller === controller) {
        state.controller = null;
        setBusy(root, false);
      }
    }
  };

  const requestView = (root, view) => {
    const state = states.get(root);
    if (!state) return;
    state.requested = view === 'list' && state.media.matches ? 'list' : 'grid';
    if (state.requested === 'grid') {
      state.controller?.abort();
      state.controller = null;
      setBusy(root, false);
      paint(root, 'grid');
      return;
    }
    loadList(root, state);
  };

  const init = (root) => {
    let state = states.get(root);
    if (!state) {
      const media = window.matchMedia(isSearch(root) ? '(min-width: 1440px)' : '(min-width: 990px)');
      state = { media, requested: 'grid', controller: null };
      states.set(root, state);
      media.addEventListener('change', () => {
        if (!media.matches) requestView(root, 'grid');
      });
      if (!isSearch(root) && new URL(window.location.href).searchParams.get('szh_layout') === 'list') {
        requestView(root, 'list');
      } else paint(root, 'grid');
    } else if (state.requested === 'list') {
      loadList(root, state);
    }
  };

  const initAll = () => document.querySelectorAll(roots).forEach(init);
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-szh-view-button]');
    const root = button?.closest(roots);
    if (root) requestView(root, button.dataset.szhViewButton);
  });
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('collection:products-updated', initAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
})();
